import { isNumber } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'reactstrap';
import { DeepPartial } from 'tsdef';
import { gql, useQuery } from 'urql';
import { Loading } from '.';
import { HacpackQuery, HacpackQueryHandlingArguments, Handling } from '../types/graph';

const handlingRiskAssessmentQuery = gql<HacpackQuery, HacpackQueryHandlingArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        id
        fei
        cei
        standardDistance
        terminal {
            ceiLimit
            feiLimit
            radiusLimit
        }
    }
}`;

type HandlingRiskAssessmentProps = { handling: DeepPartial<Handling> };

const HandlingRiskAssessment = ({ handling: { id } }: HandlingRiskAssessmentProps) => {
    // @ts-ignore
    const [result] = useQuery({
        query: handlingRiskAssessmentQuery,
        variables: { id: id as number },
        pause: id === undefined
    });

    const { t } = useTranslation('common');

    const { data, fetching, error } = result;
    const handling = data?.handling;
    const terminal = handling?.terminal;

    const fei = useMemo(() => {
        if (handling === null || handling === undefined || terminal === null || terminal === undefined) {
            return null;
        }

        const { fei } = handling;

        if (!isNumber(fei)) {
            return <>-</>;
        }

        const { feiLimit } = terminal;

        if (isNumber(fei) && isNumber(feiLimit) && fei > feiLimit) {
            return <strong>{fei}</strong>
        }

        return <>{fei}</>

    }, [handling, terminal]);

    const cei = useMemo(() => {
        if (handling === null || handling === undefined || terminal === null || terminal === undefined) {
            return null;
        }

        const { cei } = handling;

        if (!isNumber(cei)) {
            return <>-</>;
        }

        const { ceiLimit } = terminal;

        if (isNumber(ceiLimit) && cei > ceiLimit) {
            return <strong>{cei}</strong>
        }

        return <>{cei}</>

    }, [handling, terminal]);

    const standardDistance = useMemo(() => {
        if (handling === null || handling === undefined || terminal === null || terminal === undefined) {
            return null;
        }

        const { standardDistance } = handling;

        if (!isNumber(standardDistance)) {
            return <>-</>;
        }

        const { radiusLimit } = terminal;

        if (isNumber(radiusLimit) && standardDistance > radiusLimit) {
            return <strong>{standardDistance} m</strong>
        }
        return <>{ standardDistance } m</>

    }, [handling, terminal]);


    const content = useMemo(() => {
        if (error !== undefined) {
            return <Alert color="danger">{t('Si è verificato un errore durante il recupero dei dati sull\'Analisi del rischio.')}</Alert>
        }

        if (fetching === true) {
            return <Loading />;
        }

        return <dl className={'row'}>
            <dt className={'col-sm-2'}>{t('FEI')}</dt><dd className={'col-sm-2'}>{fei}</dd>
            <dt className={'col-sm-2'}>{t('CEI')}</dt><dd className={'col-sm-2'}>{cei}</dd>
            <dt className={'col-sm-2'}>{t('Distanza Standard')}</dt><dd className={'col-sm-2'}>{standardDistance}</dd>
        </dl>;
    }, [error, fetching, fei, cei, standardDistance, t])

    if (!fetching && (id === undefined || fei === undefined || cei === undefined || standardDistance === undefined)) {
        return null;
    }

    return <>
        <h4>{t('Analisi del rischio')}</h4>
        {content}
    </>
};

export default HandlingRiskAssessment;